'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowUpRight, Video, Users, PlayCircle, BookOpen, CalendarDays, Infinity as InfinityIcon } from 'lucide-react';
import { subscriptionPlans } from '@/config/subscriptions';
import BundleOfferStep from './BundleOfferStep';
import DetailBackLink from './DetailBackLink';
import styles from './PricingSectionV2.module.css';

export interface PricingPreferencesV2 {
  track: 'live' | 'vod';
  livePlanId?: 'starter' | 'progress';
  vodBundle?: boolean;
  vodPlanId?: 'trial_vod' | 'weekly_vod' | 'vod_unlimited';
  flowVersion: 'pricing_v2';
}

type LiveStep = 'intro' | 'bundle';

export interface PricingSectionV2Props {
  mode: 'landing' | 'join';
  /** Called when LIVE flow completes (join mode only). */
  onJoinLiveComplete?: (prefs: PricingPreferencesV2) => void;
}

const FLOW_VERSION = 'pricing_v2';

const LIVE_FEATURES = [
  {
    icon: Video,
    text: 'Join live Zoom sessions with direct coaching and real-time feedback.',
  },
  {
    icon: Users,
    text: 'I coach a small live group, so the work can stay specific: different levels, individual needs, and long-term technique.',
  },
  {
    icon: PlayCircle,
    text: 'Every attended class includes replay access so you can revisit sessions anytime through the VOD library.',
  },
] as const;

const LIVE_PLAN_OPTIONS = [
  {
    tier: 'starter' as const,
    title: 'Once weekly',
    popular: false,
  },
  {
    tier: 'progress' as const,
    title: 'Twice weekly',
    popular: true,
  },
];

function getLivePlanMeta(tier: 'starter' | 'progress'): string {
  const plan = subscriptionPlans[tier];
  return `${plan.liveSessionsPerMonth} live sessions per month`;
}

function getLivePlanTitle(tier: 'starter' | 'progress'): string {
  return LIVE_PLAN_OPTIONS.find((option) => option.tier === tier)?.title ?? tier;
}

const VOD_PLAN_OPTIONS = [
  {
    id: 'weekly_vod' as const,
    title: '3 Recordings',
    meta: 'Monthly unlocks, your pace',
    popular: true,
  },
  {
    id: 'vod_unlimited' as const,
    title: 'Unlimited VOD',
    meta: 'Full library, all classes',
    popular: false,
  },
] as const;

const VOD_FEATURES = [
  {
    id: 'schedule',
    icon: PlayCircle,
    text: 'Train on your schedule with polished class recordings you can revisit anytime, anywhere.',
  },
  {
    id: 'library',
    icon: BookOpen,
    text: 'A growing library of backbends, back flexibility, and splits, structured the way I coach live.',
  },
  {
    id: 'rhythm',
    icon: CalendarDays,
    text: 'Choose a monthly rhythm: three recordings, or the full library.',
  },
] as const;

function IntroFeatureIcon({ icon: Icon }: { icon: typeof Video }) {
  return (
    <span className={styles.introListIconWrap} aria-hidden="true">
      <Icon className={styles.introListIcon} size={20} strokeWidth={1.75} />
    </span>
  );
}

export default function PricingSectionV2({ mode, onJoinLiveComplete }: PricingSectionV2Props) {
  const router = useRouter();
  const sectionRef = useRef<HTMLElement>(null);
  const nudgedRef = useRef(false);
  const livePlanCardsRef = useRef<HTMLDivElement>(null);
  const vodPlanCardsRef = useRef<HTMLDivElement>(null);
  const liveCardNudgePlayedRef = useRef(false);
  const vodCardNudgePlayedRef = useRef(false);

  const [track, setTrack] = useState<'live' | 'vod'>('live');
  const [liveStep, setLiveStep] = useState<LiveStep>('intro');
  const [liveTier, setLiveTier] = useState<'starter' | 'progress' | null>(null);
  const [cardsShaking, setCardsShaking] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (nudgedRef.current) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            nudgedRef.current = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      },
      { threshold: [0.35, 0.5] }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (track === 'live' && liveStep !== 'intro') return;

    const nudgePlayedRef = track === 'live' ? liveCardNudgePlayedRef : vodCardNudgePlayedRef;
    if (nudgePlayedRef.current) return;

    const el = track === 'live' ? livePlanCardsRef.current : vodPlanCardsRef.current;
    if (!el) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    let dwellTimer: ReturnType<typeof setTimeout> | undefined;
    let shakeEndTimer: ReturnType<typeof setTimeout> | undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.4);
        if (visible) {
          if (!dwellTimer && !nudgePlayedRef.current) {
            dwellTimer = setTimeout(() => {
              nudgePlayedRef.current = true;
              setCardsShaking(true);
              shakeEndTimer = setTimeout(() => setCardsShaking(false), 2000);
            }, 8000);
          }
        } else if (dwellTimer) {
          clearTimeout(dwellTimer);
          dwellTimer = undefined;
        }
      },
      { threshold: [0.4, 0.6] }
    );

    obs.observe(el);
    return () => {
      obs.disconnect();
      if (dwellTimer) clearTimeout(dwellTimer);
      if (shakeEndTimer) clearTimeout(shakeEndTimer);
      setCardsShaking(false);
    };
  }, [track, liveStep]);

  const resetLiveToIntro = useCallback(() => {
    setLiveStep('intro');
    setLiveTier(null);
  }, []);

  const handleTrackChange = useCallback(
    (next: 'live' | 'vod') => {
      setCardsShaking(false);
      setTrack(next);
      resetLiveToIntro();
    },
    [resetLiveToIntro]
  );

  const handlePickTier = useCallback(
    (tier: 'starter' | 'progress') => {
      if (mode === 'landing') {
        // Landing visitors get a dedicated page (parity with VOD detail pages)
        router.push(`/pricing/live/${tier}`);
        return;
      }
      // Join mode keeps the inline bundle step so the user is handed back to
      // the registration form on the same page.
      setLiveTier(tier);
      setLiveStep('bundle');
    },
    [mode, router]
  );

  const handleBundleChoice = useCallback(
    (vodBundle: boolean) => {
      if (!liveTier || mode !== 'join' || !onJoinLiveComplete) return;
      onJoinLiveComplete({
        track: 'live',
        livePlanId: liveTier,
        vodBundle,
        flowVersion: FLOW_VERSION,
      });
    },
    [liveTier, mode, onJoinLiveComplete]
  );

  const liveBack = () => {
    if (liveStep === 'bundle') {
      setLiveStep('intro');
      setLiveTier(null);
    }
  };

  const handleBundleOfferClick = useCallback(() => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleBundleOfferKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleBundleOfferClick();
      }
    },
    [handleBundleOfferClick]
  );

  const renderLive = () => {
    if (liveStep === 'intro') {
      return (
        <div className={styles.contentPane} key="intro">
          <div className={styles.liveIntroLayout}>
            <div
              ref={livePlanCardsRef}
              className={`${styles.choiceRow} ${styles.choiceRowPace} ${
                cardsShaking ? styles.choiceRowPaceNudge : ''
              }`}
            >
              {LIVE_PLAN_OPTIONS.map(({ tier, title, popular }) => {
                const plan = subscriptionPlans[tier];
                return (
                  <button
                    key={tier}
                    type="button"
                    className={`${styles.choiceCard} ${styles.choiceCardLive} ${
                      popular ? styles.choiceCardPopular : ''
                    }`}
                    onClick={() => handlePickTier(tier)}
                  >
                    {popular && <span className={styles.popularChip}>Most popular</span>}
                    <span className={styles.choiceCardBody}>
                      <span className={styles.choiceCardTitle}>{title}</span>
                      <span className={styles.choiceCardPrice}>${plan.basePrice}/mo</span>
                      <span className={styles.choiceCardMeta}>
                        {plan.liveSessionsPerMonth} live sessions / month
                      </span>
                    </span>
                    <ArrowUpRight className={styles.choiceArrow} size={20} aria-hidden="true" />
                  </button>
                );
              })}
            </div>

            <ul className={styles.introList}>
              {LIVE_FEATURES.map(({ icon, text }) => (
                <li key={text} className={styles.introListItem}>
                  <IntroFeatureIcon icon={icon} />
                  <span>{text}</span>
                </li>
              ))}
              <li
                className={`${styles.introListItem} ${styles.bundleOfferItem} ${
                  mode === 'join' ? styles.bundleOfferItemInteractive : ''
                }`}
                {...(mode === 'join'
                  ? {
                      role: 'button' as const,
                      tabIndex: 0,
                      'aria-label': 'Unlimited VOD bundle. Scroll up to choose a live plan',
                      onClick: handleBundleOfferClick,
                      onKeyDown: handleBundleOfferKeyDown,
                    }
                  : {})}
              >
                <IntroFeatureIcon icon={InfinityIcon} />
                <div className={styles.bundleOfferBody}>
                  <span className={styles.bundleOfferTitle}>Unlimited VOD bundle</span>
                  <span className={styles.bundleOfferText}>
                    Add the full Streaming VOD library to any live plan for extra practice between
                    classes.
                  </span>
                </div>
                <div className={styles.bundleOfferPrice}>
                  <div className={styles.bundleOfferPriceRow}>
                    <span className={styles.bundleOfferAmount}>$40</span>
                    <span className={styles.bundleOfferCadence}>/mo</span>
                  </div>
                  {mode === 'landing' ? (
                    <Link
                      href="/vod"
                      className={styles.vodUnlimitedLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      VOD Unlimited ${subscriptionPlans.vod_unlimited.basePrice}/mo
                    </Link>
                  ) : (
                    <>
                      <span className={styles.bundleOfferStrike}>
                        ${subscriptionPlans.vod_unlimited.basePrice}
                      </span>
                      <span className={styles.bundleOfferTag}>Special offer</span>
                    </>
                  )}
                </div>
              </li>
            </ul>
          </div>
        </div>
      );
    }

    if (liveStep === 'bundle') {
      // Only reached in join mode (landing mode navigates to /pricing/live/[tier]).
      if (!liveTier) return null;
      const livePlan = subscriptionPlans[liveTier];

      return (
        <div className={styles.contentPane} key="bundle">
          <DetailBackLink onClick={liveBack} />
          <BundleOfferStep
            planTitle={getLivePlanTitle(liveTier)}
            planPrice={livePlan.basePrice}
            planMeta={getLivePlanMeta(liveTier)}
            onChooseBundle={handleBundleChoice}
          />
        </div>
      );
    }

    return null;
  };

  const renderVod = () => (
    <div className={styles.contentPane} key="vod">
      <div
        ref={vodPlanCardsRef}
        className={`${styles.choiceRow} ${styles.choiceRowPace} ${styles.vodPlanRow} ${
          cardsShaking ? styles.choiceRowPaceNudge : ''
        }`}
      >
        {VOD_PLAN_OPTIONS.map(({ id, title, meta, popular }) => {
          const plan = subscriptionPlans[id];
          return (
            <Link
              key={id}
              href={`/pricing/vod/${id}`}
              className={`${styles.choiceCard} ${styles.choiceCardLive} ${styles.vodLinkCard} ${
                popular ? styles.choiceCardPopular : ''
              }`}
            >
              {popular && <span className={styles.popularChip}>Most popular</span>}
              <span className={styles.choiceCardBody}>
                <span className={styles.choiceCardTitle}>{title}</span>
                <span className={styles.choiceCardPrice}>${plan.basePrice}/mo</span>
                <span className={styles.choiceCardMeta}>{meta}</span>
              </span>
              <ArrowUpRight className={styles.choiceArrow} size={20} aria-hidden="true" />
            </Link>
          );
        })}
      </div>
      <ul className={styles.introList}>
        {VOD_FEATURES.map(({ id, icon, text }) => (
          <li key={id} className={styles.introListItem}>
            <IntroFeatureIcon icon={icon} />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section ref={sectionRef} className={styles.wrapper} aria-label="Pricing options">
      <div className={styles.inner}>
        {mode === 'landing' && (
          <header className={styles.sectionHeader}>
            <h2 id="pricing-heading" className={styles.sectionTitle}>
              Train with me
            </h2>
            <p className={styles.sectionSecondary}>Live Zoom, or the recording library</p>
          </header>
        )}
        <div className={styles.segment} role="tablist" aria-label="Product track">
          <button
            type="button"
            role="tab"
            aria-selected={track === 'live'}
            className={`${styles.segmentBtn} ${track === 'live' ? styles.segmentBtnActive : ''}`}
            onClick={() => handleTrackChange('live')}
          >
            Live
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={track === 'vod'}
            className={`${styles.segmentBtn} ${track === 'vod' ? styles.segmentBtnActive : ''}`}
            onClick={() => handleTrackChange('vod')}
          >
            VOD
          </button>
        </div>
        <div className={styles.content}>{track === 'live' ? renderLive() : renderVod()}</div>
      </div>
    </section>
  );
}
